import { Table } from 'antd';
import { useEffect, useState } from 'react';
import { sendSingleRequest } from '../../apis';
import WeatherInfoModal from '../../components/farm-modals/WeatherInfoModal';
import { ModalComponent, Spinner, Title } from '../../components/shared';
import weather_icon from '../../images/weather-icon.png';
import { IWeatherConditionResource } from '../../entities/general.entities';
import { useSelector } from 'react-redux';
import { selectLang } from '../../store/ui/ui.selector';
import { TLang } from '../../entities/ui.entities';
import { translate } from '../../lib/lang.helper';
import FilesViewModal from '../../components/shared/pictures-wall/FilesViewModal';
import { IBioSecurity } from '../../entities/task.entities';

const columns = (lang: TLang | undefined) => [
  {
    key: 'farm',
    title: translate(lang, 'Farm'),
    render: (x: IBioSecurity) => <span>{x.farm.name}</span>,
  },
  {
    key: 'line',
    title: translate(lang, 'Line'),
    render: (x: IBioSecurity) => <span>{x.line.line_name}</span>,
  },
  {
    key: 'user',
    title: translate(lang, 'Reporter'),
    render: (x: IBioSecurity) => <span>{x.user?.name}</span>,
  },
];

const BiosecurityPage = () => {
  const lang = useSelector(selectLang);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IBioSecurity[]>();
  const [comment, setComment] = useState<string>();
  const [images, setImages] = useState<string[]>();
  const [weather, setWeather] = useState<IWeatherConditionResource>();

  useEffect(() => {
    let isIn = true;

    setLoading(true);
    sendSingleRequest({}, 'GET', 'api/report/biosecurity', true).then(res => {
      if (isIn) {
        if (res.status) {
          setLoading(false);
          setData(res.data);
        } else {
          alert(res.data?.message ?? 'Failed to load');
        }
      }
    });

    return () => {
      isIn = false;
    };
  }, []);

  const lastColumns = [
    {
      key: 'comment',
      title: translate(lang, 'Comment'),
      render: (x: IBioSecurity) => (
        <div
          className='btn__modal'
          onKeyDown={() => undefined}
          onClick={() => setComment(x.comment ?? 'No comments yet')}
        >
          {translate(lang, 'View')}
        </div>
      ),
    },
    {
      key: 'images',
      title: translate(lang, 'Images & PDFs'),
      render: (x: IBioSecurity) =>
        x.image_pdf_files && x.image_pdf_files.length > 0 ? (
          <div
            className='btn__modal'
            onKeyDown={() => undefined}
            onClick={() => setImages(x.image_pdf_files!)}
          >
            View
          </div>
        ) : (
          <></>
        ),
    },
    {
      key: 'weather',
      title: translate(lang, 'Weather'),
      render: (x: IBioSecurity) =>
        !x.weather_condition ? (
          <></>
        ) : (
          <div
            className='btn__modal'
            style={{ display: 'flex' }}
            onClick={() => setWeather(x.weather_condition!)}
          >
            <img
              className='ant-image-img'
              src={weather_icon}
              alt='weather'
              style={{ maxWidth: '20px' }}
            />
            <span style={{ marginLeft: '5px' }}>{translate(lang, 'View')}</span>
          </div>
        ),
    },
  ];

  const bioTableColumns = [
    ...columns(lang),
    {
      key: 'type',
      title: translate(lang, 'Type'),
      render: (x: IBioSecurity) => <span>{x.content_type}</span>,
    },
    ...lastColumns,
  ];
  const waterTableColumns = [
    ...columns(lang),
    {
      key: 'Mussel Larvae',
      title: translate(lang, 'Mussel Larvae'),
      render: (x: IBioSecurity) => <span>{x.mussel_larvae}</span>,
    },
    {
      key: 'Plankton',
      title: translate(lang, 'Plankton'),
      render: (x: IBioSecurity) => <span>{x.plankton}</span>,
    },
    {
      key: 'Biofouling Larvae',
      title: translate(lang, 'Biofouling Larvae'),
      render: (x: IBioSecurity) => <span>{x.biofouling_larvae}</span>,
    },
    ...lastColumns,
  ];

  return (
    <div className='bg-secondary'>
      <div className='container performance-page pb-32'>
        {loading && (
          <div className='loader'>
            <Spinner />
          </div>
        )}
        <div className='pt-32'>
          <div className='mb-24'>
            <Title size={6} color='black' align='default' fontWeight={700}>
              {translate(lang, 'Biosecurity Reports')}
            </Title>
            <div className='content'>
              <Table
                className='table table--isFarm pl-8 pr-8'
                rowKey={'id'}
                columns={bioTableColumns}
                dataSource={data?.filter(x => x.rel_type === 'BIOSECURITY')}
                pagination={false}
              />
            </div>
          </div>
          <div className='mb-24'>
            <Title size={6} color='black' align='default' fontWeight={700}>
              {translate(lang, 'Water Quality Reports')}
            </Title>
            <div className='content'>
              <Table
                className='table table--isFarm pl-8 pr-8'
                rowKey={'id'}
                columns={waterTableColumns}
                dataSource={data?.filter(x => x.rel_type === 'WATER_QUALITY')}
                pagination={false}
              />
            </div>
          </div>
        </div>
      </div>
      <ModalComponent
        visible={comment !== undefined}
        onCancel={() => setComment(undefined)}
        type=''
        title={translate(lang, 'Comment')}
        text={translate(lang, comment ?? '')}
      />
      {images !== undefined && (
        <FilesViewModal
          visible={true}
          files={images}
          onClose={() => setImages(undefined)}
        />
      )}
      {weather !== undefined && (
        <WeatherInfoModal
          data={weather}
          visible={true}
          title='Weather Condition'
          onClose={() => setWeather(undefined)}
        />
      )}
    </div>
  );
};

export default BiosecurityPage;
